//changed due to package upgrade
import React, { useState, useEffect } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material';
import "react-quill-new/dist/quill.snow.css";
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useDropzone } from 'react-dropzone';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import styled from '@emotion/styled';
import { ParentAccountInfoMain } from '@projectK/MainComponents';
const AvatarViewWrapper = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    cursor: 'pointer',
    '& .profile-edit-icon': {
      position: 'absolute',
      bottom: 0,
      right: 0,
      zIndex: 1,
      border: `solid 2px ${theme.palette.background.paper}`,
      backgroundColor: alpha(theme.palette.primary.main, 0.7),
      color: theme.palette.primary.contrastText,
      borderRadius: '50%',
      width: 26,
      height: 26,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      '& .MuiSvgIcon-root': {
        fontSize: 16,
      },
    },
    '&.dropzone': {
      outline: 0,
      '&:hover .profile-edit-icon, &:focus .profile-edit-icon': {
        display: 'flex',
      },
    },
  };
});

const parentValidationSchema = yup.object({
  first_name: yup.string().nullable().required(<IntlMessages id='validation.firstNameRequired'/>),
  last_name: yup.string().nullable().required(<IntlMessages id='validation.lastNameRequired'/>),
  user_mobile: yup
    .string()
    .matches(
      /^\d{3}-\d{3}-\d{4}$/,
      () => <IntlMessages id='validation.invalidPhoneNumber'/>,
    )
    .nullable()
    .optional(),
  email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      () => <IntlMessages id='validation.invalidEmailFormat'/>,
    )
    .typeError(<IntlMessages id='validation.invalidEmail'/>)
    .required(<IntlMessages id='validation.emailRequired' />),
  address: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong' />)
    .nullable()
    .optional(),
    zip_code: yup
    .string()
    .nullable()
    .optional()
    .matches(
      /^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/,
      () => <IntlMessages id='validation.zipCodeFormat' />
    ),
      extented_grace_period: yup
        .number()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value
        )
        .nullable()
        .typeError('Grace Period must be a number.')
        .min(0, 'Grace Period cannot be negative.'),
});
const ParentAccountInfo = ({ settings, fetchData, isMobile, roleData, hasPermission, rolePermissions }) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const dropzone = useDropzone({
    accept: {
      'image/png': ['.png', '.jpeg', '.jpg'],
    },
    onDrop: (acceptedFiles) => {
      // Append new files to the existing uploadedFiles array
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      ]);
    },
  });
  useEffect(() => {
    setUploadedFiles(dropzone.acceptedFiles);
  }, [dropzone.acceptedFiles]);

  const { user } = useAuthUser();
  const [isFullBioVisible, setIsFullBioVisible] = useState(false);

  const handleReadMore = () => {
    setIsFullBioVisible(true);
  };

  const handleReadLess = () => {
    setIsFullBioVisible(false);
  };

  let bioContent = '';
  if (settings?.biodata) {
    if (isFullBioVisible) {
      bioContent = settings.biodata;
    } else {
      bioContent = settings.biodata.slice(0, 200);
    }
  }

  const handleMobileFormat = (event, name) => {
    let inputValue = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters
    let formattedValue = '';

    // Ensure the first group of digits is not longer than 3 digits
    if (inputValue.length > 3) {
      inputValue = inputValue.slice(0, 3) + '-' + inputValue.slice(3);
    }

    // Ensure the second group of digits is not longer than 6 digits
    if (inputValue.length > 7) {
      inputValue = inputValue.slice(0, 7) + '-' + inputValue.slice(7);
    }

    // Ensure the total length does not exceed 12 characters
    formattedValue = inputValue;

    // Update form field value
    event.target.value = formattedValue; // Set input value to formatted value

    if (name === 'user_mobile') {
      return { name: 'user_mobile', value: formattedValue || null };
    } else if (name === 'parent1_mobile') {
      return { name: 'parent1_mobile', value: formattedValue || null };
    } else if (name === 'parent2_mobile') {
      return { name: 'parent2_mobile', value: formattedValue || null };
    } else if (name === 'emergency_contact_number') {
      return {
        name: 'emergency_contact_number',
        value: formattedValue || null,
      };
    }
  };

  return (
    <ParentAccountInfoMain
      dropzone={dropzone}
      uploadedFiles={uploadedFiles}
      settings={settings}
      isFullBioVisible={isFullBioVisible}
      handleReadLess={handleReadLess}
      handleReadMore={handleReadMore}
      companyTheme={companyTheme}
      isMobile={isMobile}
      parentValidationSchema={parentValidationSchema}
      fetchData={fetchData}
      infoViewActionsContext={infoViewActionsContext}
      handleMobileFormat={handleMobileFormat}
      AvatarViewWrapper={AvatarViewWrapper}
      user={user}
      bioContent={bioContent}
      roleData={roleData}
      rolePermissions={rolePermissions}
      hasPermission={hasPermission}
    />
  );
};

export default ParentAccountInfo;

ParentAccountInfo.propTypes = {
  settings: PropTypes.object,
  fetchData: PropTypes.func,
  isMobile: PropTypes.bool,
  roleData: PropTypes.object,
  rolePermissions: PropTypes.object,
  hasPermission: PropTypes.func,
};
